/**
 * Formatting utilities for contractor app.
 */

import type { TaskStatus, LaborBucket, Minutes } from 'scheduler-types';

/**
 * Format duration in minutes for display.
 * @example formatDuration(270) => "4h 30m"
 */
export function formatDuration(minutes: Minutes): string {
  if (minutes === 0) return '0m';
  if (minutes < 60) return `${minutes}m`;

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (remainingMinutes === 0) return `${hours}h`;
  return `${hours}h ${remainingMinutes}m`;
}

/**
 * Format an estimate range for display.
 * @example formatEstimateRange(60, 120) => "1-2h"
 */
export function formatEstimateRange(min: Minutes, max: Minutes): string {
  if (min === max) return formatDuration(min);

  // Convert to hours if both are >= 60
  if (min >= 60 && max >= 60) {
    const minHours = Math.round(min / 60);
    const maxHours = Math.round(max / 60);
    return `${minHours}-${maxHours}h`;
  }

  return `${formatDuration(min)} - ${formatDuration(max)}`;
}

/**
 * Format a timestamp for display.
 * @example formatTime("2026-02-01T14:30:00Z") => "2:30 PM"
 */
export function formatTime(timestamp: string): string {
  const date = new Date(timestamp);
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/**
 * Format a date for display.
 * @example formatDate("2026-02-01T14:30:00Z") => "Feb 1"
 */
export function formatDate(timestamp: string): string {
  const date = new Date(timestamp);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Format scheduled time range.
 * @example formatScheduledRange(start, end) => "Feb 1, 2:30 PM - 4:30 PM"
 */
export function formatScheduledRange(
  start: string | null,
  end: string | null
): string {
  if (!start) return 'Not scheduled';
  if (!end) return `${formatDate(start)} ${formatTime(start)}`;

  const startDate = new Date(start);
  const endDate = new Date(end);

  // Same day
  if (startDate.toDateString() === endDate.toDateString()) {
    return `${formatDate(start)}, ${formatTime(start)} - ${formatTime(end)}`;
  }

  // Different days
  return `${formatDate(start)} ${formatTime(start)} - ${formatDate(end)} ${formatTime(end)}`;
}

/**
 * Get human-readable status label.
 */
export function getStatusLabel(status: TaskStatus): string {
  const labels: Record<TaskStatus, string> = {
    pending: 'Pending',
    ready: 'Ready',
    staged: 'Staged',
    partial_staged: 'Partial',
    in_progress: 'In Progress',
    complete: 'Complete',
    cancelled: 'Cancelled',
    abandoned: 'Abandoned',
  };
  return labels[status] ?? status;
}

/**
 * Get human-readable labor bucket label.
 */
export function getLaborBucketLabel(bucket: LaborBucket): string {
  const labels: Record<LaborBucket, string> = {
    mechanical_engineering: 'Mechanical Eng',
    electrical_engineering: 'Electrical Eng',
    software_design: 'Software Design',
    image_formation_design: 'Image Formation',
    software_development: 'Software Dev',
    fabrication: 'Fabrication',
    electrical_assembly: 'Electrical Assembly',
    final_assembly: 'Final Assembly',
    testing_qc: 'Testing/QC',
    packing_shipping: 'Pack & Ship',
    procuring: 'Procurement',
    waiting_for_parts: 'Awaiting Parts',
    awaiting_confirmation: 'Awaiting Confirmation',
    awaiting_shipment: 'Awaiting Shipment',
    awaiting_receipt: 'Awaiting Receipt',
    stage_parts: 'Stage Parts',
    supply_chain_release: 'Supply Chain',
  };
  return labels[bucket] ?? bucket;
}
